import type { ParticipantStatus, ProgressTree, ProgressStep, ProgressNode, SessionEvent } from '@localagent/sdk';
import type { CognitiveToolType } from '../../types/reasoning-types';
import { COGNITIVE_TOOL_LABELS } from './progress-tree';
import { formatPersonaName } from '../../utils/persona-display';
import { fanoutFor } from './turn-status';

export type SubAgentFeed = {
  nodeId: string;
  label: string;
  status: 'running' | 'completed' | 'failed' | 'stopped';
  summary?: string;
  fanout?: { done: number; total: number };
  participantId?: string;
  participantStatus?: ParticipantStatus;
  background?: boolean;
  items: ActivityFeedItem[];
};

export type ActivityFeedItem =
  | { kind: 'assistant_turn'; key: string; step: ProgressStep }
  | {
      kind: 'tool_invocation';
      key: string;
      step: ProgressStep;
      child?: SubAgentFeed;
    }
  | { kind: 'sub_agent'; key: string; feed: SubAgentFeed };

function subAgentLabel(node: ProgressNode): string {
  if (node.cognitiveToolType && node.cognitiveToolType in COGNITIVE_TOOL_LABELS) {
    return COGNITIVE_TOOL_LABELS[node.cognitiveToolType as CognitiveToolType];
  }
  // Process-graph steps carry a humanized label (Triage / Plan); prefer it over
  // the shared session persona so steps don't all read as "Software Engineer".
  if (node.stepId || (node.label && node.label !== formatPersonaName(node.personaName ?? ''))) {
    if (node.label) return node.label;
  }
  if (node.personaName) return formatPersonaName(node.personaName);
  return node.label || 'Sub-agent';
}

function nodeToSubAgentFeed(node: ProgressNode, siblings: ProgressNode[], keyPrefix: string): SubAgentFeed {
  return {
    nodeId: node.id,
    label: subAgentLabel(node),
    status: node.status,
    summary: node.conclusionSummary ?? node.promptPreview,
    fanout: fanoutFor(node, siblings) ?? undefined,
    participantId: node.participantId !== undefined && node.participantId === node.id ? node.id : undefined,
    participantStatus: node.participantId !== undefined && node.participantId === node.id ? node.participantStatus : undefined,
    background: node.background,
    items: mapStepsAndChildren(node.steps, node.children, `${keyPrefix}${node.id}-`),
  };
}

export function mapStepsAndChildren(
  steps: ProgressStep[],
  childNodes: ProgressNode[],
  keyPrefix = '',
): ActivityFeedItem[] {
  const nodesById = new Map(childNodes.map((n) => [n.id, n]));
  const nestedIds = new Set<string>();
  const entries: Array<{ ts: string; item: ActivityFeedItem }> = [];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const key = `${keyPrefix}${step.type}-${i}`;

    if (step.type === 'llm_call') {
      entries.push({ ts: step.timestamp, item: { kind: 'assistant_turn', key, step } });
      continue;
    }

    if (step.type === 'tool_call') {
      let child: SubAgentFeed | undefined;
      if (step.childIdAtStart) {
        const spawned = nodesById.get(step.childIdAtStart);
        if (spawned) {
          child = nodeToSubAgentFeed(spawned, childNodes, keyPrefix);
          nestedIds.add(spawned.id);
        }
      }
      entries.push({
        ts: step.timestamp,
        item: { kind: 'tool_invocation', key, step, child },
      });
      continue;
    }

  }

  for (const child of childNodes) {
    if (nestedIds.has(child.id)) continue;
    entries.push({
      ts: child.startedAt,
      item: {
        kind: 'sub_agent',
        key: `${keyPrefix}agent-${child.id}`,
        feed: nodeToSubAgentFeed(child, childNodes, keyPrefix),
      },
    });
  }

  entries.sort((a, b) => a.ts.localeCompare(b.ts));
  return entries.map((e) => e.item);
}

export function mapProgressToActivityFeed(tree: ProgressTree | null | undefined): ActivityFeedItem[] {
  if (!tree) return [];

  if (tree.nodes.length === 0) {
    return mapStepsAndChildren(tree.steps, [], '');
  }

  const byId = new Map(tree.nodes.map((n) => [n.id, n]));
  const rootNodes = tree.nodes.filter((n) => !n.parentId || !byId.has(n.parentId));

  if (tree.steps.length === 0) {
    const items: ActivityFeedItem[] = [];
    for (const root of rootNodes) {
      items.push(...mapStepsAndChildren(root.steps, root.children, `root-${root.id}-`));
    }
    return items;
  }

  return mapStepsAndChildren(tree.steps, rootNodes, '');
}

export function countToolInvocations(items: ActivityFeedItem[]): number {
  let n = 0;
  const walk = (list: ActivityFeedItem[]) => {
    for (const item of list) {
      if (item.kind === 'tool_invocation') {
        n += 1;
        if (item.child) walk(item.child.items);
      } else if (item.kind === 'sub_agent') {
        walk(item.feed.items);
      }
    }
  };
  walk(items);
  return n;
}

export function formatInlineArgs(preview: string): string {
  if (!preview || preview.trim() === '') return '';
  const trimmed = preview.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const parts: string[] = [];
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
          if (value === null || value === undefined || value === '') continue;
          if (typeof value === 'string') {
            parts.push(`${key}=${value}`);
          } else {
            parts.push(`${key}=${JSON.stringify(value)}`);
          }
        }
        return parts.join(' ');
      }
    } catch {
      return trimmed;
    }
  }
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      return JSON.stringify(JSON.parse(trimmed) as unknown);
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export function formatReadablePayload(preview: string | undefined): string {
  if (preview == null || preview === '') return '';
  const trimmed = preview.trim();
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.stringify(JSON.parse(trimmed) as unknown, null, 2);
    } catch {
      return preview;
    }
  }
  return preview;
}

export type ToolEventSeqs = {
  startSeq?: number;
  resultSeq?: number;
};

export function findToolEventSeqs(events: readonly SessionEvent[], step: ProgressStep): ToolEventSeqs {
  if (step.type !== 'tool_call' || !step.toolName) return {};

  // The step's timestamp is the start event's created_at. Tool name and
  // iteration repeat on every turn, so the first match in the session is the
  // previous turn's call.
  let startSeq: number | undefined;
  for (const ev of events) {
    if (ev.event_type !== 'tool_call_start') continue;
    if (ev.created_at !== step.timestamp) continue;
    const data = ev.data as Record<string, unknown>;
    if (data.tool_name !== step.toolName) continue;
    if (step.iteration != null && data.iteration != null && data.iteration !== step.iteration) {
      continue;
    }
    startSeq = ev.sequence_num;
    break;
  }
  if (startSeq == null) return {};

  let resultSeq: number | undefined;
  for (const ev of events) {
    if (ev.sequence_num <= startSeq) continue;
    if (ev.event_type !== 'tool_call_result') continue;
    const data = ev.data as Record<string, unknown>;
    if (data.tool_name !== step.toolName) continue;
    if (step.iteration != null && data.iteration != null && data.iteration !== step.iteration) {
      continue;
    }
    resultSeq = ev.sequence_num;
    break;
  }

  return { startSeq, resultSeq };
}

export function feedItemEventSeqs(
  item: ActivityFeedItem,
  events: readonly SessionEvent[],
  includeNested: boolean,
): number[] {
  const seqs: number[] = [];
  if (item.kind === 'tool_invocation') {
    const { startSeq, resultSeq } = findToolEventSeqs(events, item.step);
    if (startSeq != null) seqs.push(startSeq);
    if (resultSeq != null) seqs.push(resultSeq);
    if (includeNested && item.child) {
      for (const child of item.child.items) {
        seqs.push(...feedItemEventSeqs(child, events, true));
      }
    }
  } else if (item.kind === 'sub_agent' && includeNested) {
    for (const child of item.feed.items) {
      seqs.push(...feedItemEventSeqs(child, events, true));
    }
  }
  return seqs;
}
